import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  Box,
  CircularProgress,
  Alert,
  Typography,
  IconButton,
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { getAvailableGames, updateGameInPromotion } from '../../../Provider/parseDataProvider';

export const EditGameDialog = ({ open, onClose, onSubmit, gameData }) => {
  const [formData, setFormData] = useState({
    gameId: '',
    gameName: '',
    gameCategory: 'newLaunch', // 'newLaunch' or 'comingSoon'
    popUpImage: null, // File object for popup image
    currentPopUpImageUrl: '', // Current image URL from database
  });
  const [gameOptions, setGameOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const notify = useNotify();

  // Initialize form data when gameData prop changes
  useEffect(() => {
    if (gameData && open) {
      setFormData({
        gameId: gameData.id,
        gameName: gameData.name,
        gameCategory: gameData.section,
        popUpImage: null, // No new file uploaded initially
        currentPopUpImageUrl: gameData.popUpImage || '', // Existing image URL
      });
      setImagePreview(null); // No preview for existing images, use currentPopUpImageUrl instead
    }
  }, [gameData, open]);

  // Load available games when dialog opens
  useEffect(() => {
    if (open) {
      loadAvailableGames();
    }
  }, [open]);

  const loadAvailableGames = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getAvailableGames();
      
      if (response.success) {
        setGameOptions(response.data || []);
      } else {
        setError(response.message || 'Failed to load games');
      }
    } catch (err) {
      console.error('Error loading games:', err);
      setError('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const handleGameSelect = (gameId) => {
    const selectedGame = gameOptions.find(game => game.id === gameId);
    if (selectedGame) {
      console.log('Changing game selection to:', selectedGame.name, '- clearing image');
      
      setFormData({
        ...formData,
        gameId: selectedGame.id,
        gameName: selectedGame.name,
        // Clear image data when selecting different game
        popUpImage: null,
        currentPopUpImageUrl: '',
      });
      // Clear the image preview as well
      setImagePreview(null);
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type.toLowerCase())) {
        setError('Invalid file type. Only images (JPEG, PNG, GIF, WebP) are allowed.');
        return;
      }

      // Validate file size (max 512KB)
      if (file.size > 512 * 1024) {
        notify('File size too large. Maximum size is 512KB.', { type: 'error' });
        return;
      }

      setFormData({
        ...formData,
        popUpImage: file,
      });

      // Create image preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleRemoveImage = () => {
    setFormData({
      ...formData,
      popUpImage: null,
      currentPopUpImageUrl: '', // Also clear the current URL
    });
    setImagePreview(null);
  };

  const handleSubmit = async () => {
    // For Coming Soon, only image is required
    // For New Launch, gameId, gameName and image are required
    const isValidSubmission = formData.gameCategory === 'comingSoon' 
      ? formData.popUpImage 
      : (formData.gameId && formData.gameName && formData.popUpImage);

    if (isValidSubmission) {
      try {
        setUploadingImage(true);
        
        // Use the new update function that handles image upload
        const updatedGameData = {
          gameId: formData.gameId,
          gameCategory: formData.gameCategory,
          popUpImage: formData.popUpImage,
          currentPopUpImageUrl: formData.currentPopUpImageUrl,
          originalGameId: gameData.id, // Pass original game ID for updating
          originalSection: gameData.section, // Pass original section
        };
        
        await updateGameInPromotion(updatedGameData);
        
        onSubmit(updatedGameData);
        handleClose();
      } catch (error) {
        console.error('Error updating game:', error);
        setError(error.message || 'Failed to update game');
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleClose = () => {
    // Reset form on close
    setFormData({
      gameId: '',
      gameName: '',
      gameCategory: 'newLaunch',
      popUpImage: null,
      currentPopUpImageUrl: '',
    });
    setImagePreview(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: '#fff' }}>
        {formData.gameCategory === 'comingSoon' ? 'Edit Coming Soon Image' : 'Edit Game'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Game Category Radio Buttons */}
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ color: '#fff' }}>Game Category</FormLabel>
            <RadioGroup
              row
              value={formData.gameCategory}
              onChange={(e) => {
                console.log('Changing category to:', e.target.value, '- clearing image');
                setFormData({ 
                  ...formData, 
                  gameCategory: e.target.value, 
                  gameId: '', 
                  gameName: '',
                  // Clear image data when changing category
                  popUpImage: null,
                  currentPopUpImageUrl: ''
                });
                // Clear image preview as well
                setImagePreview(null);
              }}
            >
              <FormControlLabel
                value="newLaunch"
                control={<Radio />}
                label="New Launch"
                sx={{ color: '#fff' }}
              />
              <FormControlLabel
                value="comingSoon"
                control={<Radio />}
                label="Coming Soon"
                sx={{ color: '#fff' }}
              />
            </RadioGroup>
          </FormControl>

          {/* If New Launch, show games dropdown */}
          {formData.gameCategory === 'newLaunch' && !loading && (
            <FormControl fullWidth>
              <InputLabel id="game-select-label">Select Game</InputLabel>
              <Select
                labelId="game-select-label"
                value={formData.gameId}
                label="Select Game"
                onChange={(e) => handleGameSelect(e.target.value)}
              >
                {gameOptions.map((game) => (
                  <MenuItem key={game.id} value={game.id}>
                    {game.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Popup Image Upload (always shown) */}
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, color: '#fff' }}>
              Popup Image {formData.gameCategory === 'comingSoon' ? '' : '(Optional)'}
            </Typography>
            {!imagePreview && !formData.currentPopUpImageUrl ? (
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: '#f5f5f5'
                  }
                }}
                onClick={() => document.getElementById('edit-image-upload').click()}
              >
                <CloudUpload sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Click to upload popup image
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Supports: JPEG, PNG, GIF, WebP (Max 512KB)
                </Typography>
                <input
                  id="edit-image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {/* Image with delete button */}
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                  <img
                    src={imagePreview || formData.currentPopUpImageUrl}
                    alt="Preview"
                    style={{
                      width: '100%',
                      maxWidth: '200px',
                      height: 'auto',
                      borderRadius: '8px',
                      border: '1px solid #ddd'
                    }}
                  />
                  <IconButton
                    onClick={handleRemoveImage}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: '#f44336',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#d32f2f'
                      }
                    }}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Box>
                
                {/* Change Image Button - Now below the image */}
                <Button
                  variant="outlined"
                  onClick={() => document.getElementById('edit-image-upload').click()}
                  sx={{ mb: 1 }}
                >
                  Change Image
                </Button>
                
                {/* Hidden file input */}
                <input
                  id="edit-image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploadingImage}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={
            (formData.gameCategory === 'newLaunch' && !formData.gameId) ||
            !formData.popUpImage ||
            loading || uploadingImage
          }
        >
          {uploadingImage ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Updating...
            </>
          ) : (
            formData.gameCategory === 'comingSoon' ? 'Update Image' : 'Update Game'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};